// FNP-1193 — the certificate read path, plus the connection capability
// snapshot the write controls depend on.

// ⚠️ `status`, `statusDetail` AND `expirationDate` ARE SELECTED ON EVERY READ,
// and that is not redundancy — it is the design.
//
// None of the three is STORED server-side. They are read through to the
// provider on every request (FNP-1221's central decision), which is what keeps
// a certificate's status from disagreeing with the provider that owns it. A
// caller that selected them once and cached the result would reintroduce
// exactly the staleness the design pays an N+1 to avoid.
//
// ⚠️ `validationRecordWrittenAt` IS SELECTED EVEN THOUGH NOTHING RENDERS IT
// DIRECTLY. It is the clock STUCK is measured from, and its NULLNESS is what
// separates VALIDATION_NOT_WRITTEN from STUCK. The backend computes the status
// from it, but the console shows the operator *why* a certificate is in the
// state it is in, and "Flexi never recorded writing the record" is that reason.
//
// ⚠️ `validationRecords` IS SELECTED FOR THE LIST, not only the detail. When
// validation is STUCK or was never written, the record is the first thing
// anyone wants to compare against the zone — making them open a second view to
// see it is the friction this module exists to remove.
const CERTIFICATE_FIELDS = `
    id
    accountId
    connectionId
    domainName
    coveredNames
    targetType
    region
    status
    statusDetail
    expirationDate
    validationRecords {
        name
        type
        value
        domainName
        status
    }
    validationRecordWrittenAt
    validationZoneId
    stuckAfterMinutes
    issuingAwsAccountId
    createdByUsername
    created
    updated
`;

// ⚠️ THERE IS DELIBERATELY NO `accountId` ARGUMENT. The tenant is a KEY
// CONDITION on the server, built from the caller's own claim — not a filter the
// client supplies. Another tenant's certificates are in a different partition
// and are never read. Sending one would be a field the schema does not have.
export const listCertificates = `
   query listCertificates($limit: Int, $nextToken: String) {
       listCertificates(limit: $limit, nextToken: $nextToken) {
           items {${CERTIFICATE_FIELDS}}
           nextToken
       }
   }
`;

// ⚠️ THE `id` IS NOT TRUSTED BY THE SERVER EITHER. The caller's tenant comes
// from their claim and the id must begin with that tenant's segment, checked
// before any storage read. A caller who learns another tenant's certificate id
// cannot use it.
export const getCertificate = `
   query getCertificate($id: ID!) {
       getCertificate(id: $id) {${CERTIFICATE_FIELDS}}
   }
`;

// The connections a certificate can be requested through.
//
// ⚠️ `writable` IS THE GATE, AND IT IS THE EXACT ONE — NOT A PROXY.
//
// This is worth stating precisely, because the obvious guess is wrong.
// `ProviderConnectionCapabilities` carries NO certificate or ACM field: it has
// supportedRecordTypes, canWriteRecords, canCreateZones, canDeleteZones and
// canSetNameservers, and nothing else. So the natural reach is
// `canWriteRecords`, on the reasoning that requesting a certificate writes a
// validation record. That would be a proxy, and it would be the wrong one.
//
// The backend derives its certificate gate from `ProviderConnection.writable`
// and from nothing else. `flexi-domains-api/src/certificate/src/main.ts`:
//
//     const declared = connection.writable === true;
//     return new AcmIssuerAdapter(slice, {canRequest: declared, canDelete: declared, region});
//
// and `request.ts` refuses before any AWS call when `canRequest` is false. So
// `writable === true` is NECESSARY AND SUFFICIENT for passing the Flexi-side
// gate — selecting it means this console hides exactly the controls the backend
// would refuse, with no guessing in between.
//
// ⚠️ IT STILL DOES NOT PROVE THE REQUEST WILL SUCCEED, and the module never
// claims it does. `writable` is DECLARED BY THE TENANT, not probed — probing
// needs `iam:SimulatePrincipalPolicy` evaluated in the account owning the
// principal, which is the tenant's, so Flexi cannot verify it. A tenant who
// declared `writable: true` from the DNS-only instructions passes this gate and
// still fails at ACM with AccessDenied. That refusal is classified and given
// copy naming `acm:RequestCertificate`; see writeErrorMessages.
//
// ⚠️ ONE FLAG COVERS BOTH DNS WRITE AND ACM WRITE. A tenant who granted Route
// 53 write but not ACM write cannot express that, and neither this console nor
// the backend can distinguish it. That is a backend gap, not one this module
// can close.
export const listProviderConnectionsForCertificates = `
   query listProviderConnections($limit: Int, $nextToken: String) {
       listProviderConnections(limit: $limit, nextToken: $nextToken) {
           items {
               id
               displayName
               providerId
               status
               writable
           }
           nextToken
       }
   }
`;

// One connection's write declaration, for the hiding decision on a page that
// already knows which connection it is asking about.
//
// `secretRef` is deliberately NOT selected: this module never needs it, and not
// asking is one fewer place it could be logged. `capabilities` is not selected
// either — none of its five fields governs certificates.
export const getProviderConnectionCapabilities = `
   query getProviderConnection($id: ID!) {
       getProviderConnection(id: $id) {
           id
           status
           writable
       }
   }
`;
